import { useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  TextField,
  Typography,
} from '@mui/material';

import { useLinkSellerToContractMutation } from '@/apis/extended/admin/agentApi';

export type LinkSellerContractOption = {
  id: string;
  address: string;
  buyer: string;
  price: string;
  status: string;
  seller: string | null;
};

/**
 * Links a seller to a contract by email. Without this there was no way to
 * give a seller access: every contract showed "Seller: Not specified".
 */
export const LinkSellerForm = ({
  contractOptions,
  onLinked,
}: {
  contractOptions: LinkSellerContractOption[];
  onLinked?: () => void;
}) => {
  const [contractId, setContractId] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [result, setResult] = useState<{
    severity: 'success' | 'warning' | 'error';
    message: string;
  } | null>(null);
  const [linkSeller, { isLoading }] = useLinkSellerToContractMutation();
  const selected = contractOptions.find((o) => o.id === contractId) || null;

  const submit = async () => {
    if (!contractId || !email.trim()) {
      setResult({
        severity: 'warning',
        message: "Choose a contract and enter the seller's email.",
      });
      return;
    }
    try {
      const res = await linkSeller({
        contractId,
        name: name.trim(),
        email: email.trim(),
      }).unwrap();
      setResult({
        severity: res.emailSent === false ? 'warning' : 'success',
        message: res.message,
      });
      setContractId('');
      setName('');
      setEmail('');
      onLinked?.();
    } catch (error: any) {
      setResult({
        severity: 'error',
        message: error?.data?.message || 'Could not link the seller.',
      });
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        Link seller to contract
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The seller gets an account (passwordless sign-in), the contract on their
        dashboard, and an email with a link to review the offer.
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr auto' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <Autocomplete
          options={contractOptions}
          value={selected}
          onChange={(_, option) => setContractId(option?.id || '')}
          getOptionLabel={(o) => `${o.address} — ${o.buyer}`}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          filterOptions={(opts, { inputValue }) => {
            const q = inputValue.trim().toLowerCase();
            if (!q) return opts;
            return opts.filter((o) =>
              [o.address, o.buyer, o.id, o.seller || '']
                .join(' ')
                .toLowerCase()
                .includes(q)
            );
          }}
          renderOption={(props, o) => (
            <li {...props} key={o.id}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {o.address}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {o.buyer}
                  {o.price ? ` · ${o.price}` : ''} · {o.status} ·{' '}
                  {o.seller ? `seller ${o.seller}` : 'no seller'}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Contract"
              placeholder="Search by address, buyer or contract ID"
              helperText={
                selected?.seller
                  ? `Currently ${selected.seller} — linking replaces the seller.`
                  : ' '
              }
            />
          )}
          fullWidth
        />
        <TextField
          label="Seller name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
        />
        <TextField
          label="Seller email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
        />
        <Button
          variant="contained"
          onClick={() => void submit()}
          disabled={isLoading}
          sx={{ height: 56 }}
        >
          Link seller
        </Button>
      </Box>
      {result && (
        <Alert severity={result.severity} sx={{ mt: 2 }}>
          {result.message}
        </Alert>
      )}
    </Box>
  );
};
